/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChild, ComponentProps, Fragment, JSX } from 'preact';
import { BaseButton } from '../UNSAFE_BaseButton';
import { ButtonLabelLayout } from '../UNSAFE_ButtonLabelLayout';
import { forwardRef, ForwardedRef, ElementRef } from 'preact/compat';
import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';
import type { DimensionProps } from '../utils/UNSAFE_interpolations/dimensions';
import { useTooltip } from '../hooks/UNSAFE_useTooltip';
import { TestIdProps } from '../hooks/UNSAFE_useTestId';
import type { ButtonIntrinsicProps } from '../utils/UNSAFE_buttonUtils';
import { processGlobals } from '../utils/UNSAFE_buttonUtils';
import { mergeProps } from '#utils/UNSAFE_mergeProps';

type ButtonHandle = ElementRef<typeof BaseButton>;

type IntrinsicProps = Pick<UnpackSignals<JSX.IntrinsicElements['button']>, 'autofocus'>;
type WidthInterpolationProps = Pick<DimensionProps, 'width'>;
type BaseButtonProps = ComponentProps<typeof BaseButton>;

type BaseProps = IntrinsicProps &
  ButtonIntrinsicProps &
  Pick<BaseButtonProps, 'aria-haspopup'> &
  WidthInterpolationProps & {
    /**
     * Specifies the icon for the button.
     */
    children?: ComponentChild;

    /**
     * Specifies that the button element should be disabled.
     */
    isDisabled?: boolean;

    /**
     * Specifies if repeated events are dispatched for key or pointer held down
     */
    isRepeat?: boolean;

    /**
     * Specifes the button type
     */
    type?: 'button' | 'submit';

    /**
     * The ID of an element (or space separated IDs of multiple elements) that
     * describes the button.
     */
    'aria-describedby'?: string;

    /**
     * Triggered when a button is clicked, whether by keyboard, mouse, or touch events.
     */
    onAction?: ComponentProps<typeof BaseButton>['onAction'];

    /**
     * Indicates in what states the button has chrome (background and border).
     *     "ghost": "Ghost buttons are the least prominent variant. Ghost buttons are
     * useful for performing low-priority tasks, such as manipulating the UI.",
     *     "borderless"': "Borderless buttons are a more prominent variant. Borderless
     * buttons are useful for supplemental actions that require minimal emphasis.",
     *     "outlined"': "Outlined buttons are salient, but lighter weight than
     * solid buttons. Outlined buttons are useful for secondary actions.",
     *     "solid"': "Solid buttons stand out, and direct the user's attention to the
     *  most important actions in the UI.",
     *     "callToAction"': "A Call To Action (CTA) button guides the user to take or
     * complete the action that is the main goal of the page or page section. There
     * should only be one CTA button on a page at any given time.",
     *     "danger"': "A Danger button alerts the user to a dangerous situation."
     */
    variant?: 'ghost' | 'borderless' | 'outlined' | 'solid' | 'callToAction' | 'danger';
    /**
     * The size indicates how large the button is rendered.
     */
    size?: 'xs' | 'sm' | 'md' | 'lg';
  };

/**
 * Note that either an aria-label or tooltip must be specified.
 */
type VariationAccessibleLabelProps =
  | {
      /**
       * aria-label - the aria label
       */
      'aria-label': string;

      /**
       * tooltip - overrides the aria-label for the tooltip
       */
      tooltip?: string;
    }
  | {
      'aria-label'?: string;

      tooltip: string;
    };
/**
 * Icon buttons are buttons that only show an icon and no label. They direct users to initiate or take actions and work with a single tap, click, or keystroke.
 */
export const IconButton = forwardRef(
  (
    {
      variant = 'outlined',
      isDisabled = false,
      isRepeat = false,
      size = 'md',
      type: buttonType = 'button',
      autofocus,
      onAction,
      width,
      tooltip,
      testId,
      ...props
    }: BaseProps & VariationAccessibleLabelProps & TestIdProps,
    ref: ForwardedRef<ButtonHandle>
  ) => {
    const {
      globalEventProps,
      ariaProps: { 'aria-label': accessibleLabel, ...otherAriaProps }
    } = processGlobals(props);

    const { tooltipContent, tooltipProps } = useTooltip({
      text: tooltip ? tooltip : accessibleLabel,
      isDisabled
    });

    const elementDetails = {
      type: 'button' as const,
      buttonType: buttonType
    };

    return (
      <Fragment>
        <BaseButton
          {...mergeProps(tooltipProps, globalEventProps, otherAriaProps)}
          ref={ref}
          elementDetails={elementDetails}
          isDisabled={isDisabled}
          isRepeat={isRepeat}
          size={size}
          autofocus={autofocus}
          variant={variant}
          width={width}
          styling={['min', 'noUserSelect']}
          onAction={onAction}
          aria-label={accessibleLabel ?? tooltip}
          testId={testId}>
          <ButtonLabelLayout
            display={'icons'}
            startIcon={props.children}
            size={size}></ButtonLabelLayout>
        </BaseButton>
        {tooltipContent}
      </Fragment>
    );
  }
);

IconButton.displayName = 'IconButton';
